import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Dna,
  Plus,
  MagnifyingGlass,
  Copy,
  ArrowRight,
  Lightning,
  Gauge,
  ChartBar
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const SequencesPage = () => {
  const navigate = useNavigate();
  const [sequences, setSequences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showNewForm, setShowNewForm] = useState(false);
  const [showToolsPanel, setShowToolsPanel] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    sequence: '',
    sequence_type: 'dna',
    description: '',
    organism: '',
    tags: []
  });
  const [saving, setSaving] = useState(false);

  // Tools state
  const [toolSequence, setToolSequence] = useState('');
  const [primerResults, setPrimerResults] = useState(null);
  const [analysisResults, setAnalysisResults] = useState(null);
  const [codonResults, setCodonResults] = useState(null);
  const [selectedOrganism, setSelectedOrganism] = useState('E. coli');

  useEffect(() => {
    loadSequences();
  }, []);

  const loadSequences = async () => {
    try {
      const data = await api.getSequences();
      setSequences(data);
    } catch (error) {
      console.error('Failed to load sequences:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSequence = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.createSequence(formData);
      toast.success('Sequence created');
      setShowNewForm(false);
      setFormData({ name: '', sequence: '', sequence_type: 'dna', description: '', organism: '', tags: [] });
      loadSequences();
    } catch (error) {
      toast.error('Failed to create sequence');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this sequence?')) {
      try {
        await api.deleteSequence(id);
        setSequences(sequences.filter(s => s.id !== id));
        toast.success('Sequence deleted');
      } catch (error) {
        toast.error('Failed to delete sequence');
      }
    }
  };

  const handleDesignPrimers = async () => {
    if (!toolSequence || toolSequence.length < 40) {
      toast.error('Sequence must be at least 40 bp for primer design');
      return;
    }
    try {
      const result = await api.designPrimers(toolSequence);
      setPrimerResults(result);
      toast.success('Primers designed');
    } catch (error) {
      toast.error('Failed to design primers');
    }
  };

  const handleAnalyzeSequence = async () => {
    if (!toolSequence) {
      toast.error('Please enter a sequence');
      return;
    }
    try {
      const result = await api.analyzeSequence(toolSequence);
      setAnalysisResults(result);
      toast.success('Analysis complete');
    } catch (error) {
      toast.error('Failed to analyze sequence');
    }
  };

  const handleCodonOptimize = async () => {
    if (!toolSequence || toolSequence.length < 3) {
      toast.error('Please enter a valid coding sequence');
      return;
    }
    try {
      const result = await api.optimizeCodons(toolSequence, selectedOrganism);
      setCodonResults(result);
      toast.success('Codon optimization complete');
    } catch (error) {
      toast.error('Failed to optimize codons');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const formatSequence = (seq) => {
    if (!seq) return '';
    return seq.match(/.{1,60}/g)?.join('\n') || seq;
  };

  const colorizeSequence = (seq) => {
    if (!seq) return null;
    return seq.split('').map((base, idx) => (
      <span key={idx} className={`base-${base.toLowerCase()}`}>{base}</span>
    ));
  };

  const filteredSequences = sequences.filter(s =>
    s.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div data-testid="sequences-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="heading-display">Sequences</h1>
          <p className="text-zinc-500 mt-1">DNA sequence library and tools</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            className="btn-secondary flex items-center gap-2"
            onClick={() => { setShowToolsPanel(!showToolsPanel); setShowNewForm(false); }}
            data-testid="toggle-tools-btn"
          >
            <Lightning size={16} />
            {showToolsPanel ? 'Hide Tools' : 'Sequence Tools'}
          </Button>
          <Button 
            className="btn-primary flex items-center gap-2"
            onClick={() => { setShowNewForm(!showNewForm); setShowToolsPanel(false); }}
            data-testid="new-sequence-btn"
          >
            <Plus size={16} />
            Add Sequence
          </Button>
        </div>
      </div>

      {/* New Sequence Form */}
      {showNewForm && (
        <div className="card-flat p-6 mb-6" data-testid="new-sequence-form">
          <h3 className="heading-card mb-4">Add New Sequence</h3>
          <form onSubmit={handleCreateSequence} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Sequence name"
                  className="input-field w-full mt-1"
                  required
                />
              </div>
              <div>
                <Label htmlFor="sequence_type">Type</Label>
                <select
                  id="sequence_type"
                  value={formData.sequence_type}
                  onChange={(e) => setFormData({ ...formData, sequence_type: e.target.value })}
                  className="input-field w-full mt-1"
                >
                  <option value="dna">DNA</option>
                  <option value="rna">RNA</option>
                  <option value="protein">Protein</option>
                </select>
              </div>
            </div>
            <div>
              <Label htmlFor="sequence">Sequence *</Label>
              <Textarea
                id="sequence"
                value={formData.sequence}
                onChange={(e) => setFormData({ ...formData, sequence: e.target.value.toUpperCase().replace(/[^ATCGNU]/gi, '') })}
                placeholder="ATCGATCG..."
                className="input-field w-full mt-1 min-h-[100px] font-mono"
                required
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="organism">Organism</Label>
                <Input
                  id="organism"
                  value={formData.organism}
                  onChange={(e) => setFormData({ ...formData, organism: e.target.value })}
                  placeholder="e.g., E. coli"
                  className="input-field w-full mt-1"
                />
              </div>
              <div>
                <Label htmlFor="description">Description</Label>
                <Input
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description"
                  className="input-field w-full mt-1"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" className="btn-secondary" onClick={() => setShowNewForm(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-primary" disabled={saving}>
                {saving ? 'Saving...' : 'Save Sequence'}
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Sequence Tools Panel */}
      {showToolsPanel && (
        <div className="card-flat p-6 mb-6" data-testid="sequence-tools-panel">
          <h3 className="heading-card mb-4">Sequence Tools</h3>
          <div className="space-y-4">
            <div>
              <Label>Input Sequence</Label>
              <Textarea
                value={toolSequence}
                onChange={(e) => setToolSequence(e.target.value.toUpperCase().replace(/[^ATCGNU]/gi, ''))}
                placeholder="Paste your DNA sequence here (ATCG only)..."
                className="input-field w-full mt-1 min-h-[100px] font-mono"
              />
            </div>
            
            <div className="flex flex-wrap gap-2">
              <Button 
                type="button" 
                className="btn-secondary"
                onClick={handleAnalyzeSequence}
                data-testid="analyze-sequence-btn"
              >
                <ChartBar size={16} className="mr-2" /> Analyze
              </Button>
              <Button 
                type="button" 
                className="btn-secondary"
                onClick={handleDesignPrimers}
                data-testid="design-primers-btn"
              >
                <Gauge size={16} className="mr-2" /> Design Primers
              </Button>
              <div className="flex items-center gap-2">
                <select
                  value={selectedOrganism}
                  onChange={(e) => setSelectedOrganism(e.target.value)}
                  className="input-field"
                >
                  <option value="E. coli">E. coli</option>
                  <option value="S. cerevisiae">S. cerevisiae</option>
                  <option value="Human">Human</option>
                </select>
                <Button 
                  type="button" 
                  className="btn-secondary"
                  onClick={handleCodonOptimize}
                  data-testid="codon-optimize-btn"
                >
                  <Dna size={16} className="mr-2" /> Optimize Codons
                </Button>
              </div>
            </div>

            {/* Analysis Results */}
            {analysisResults && (
              <div className="mt-4 p-4 bg-zinc-50 rounded-sm" data-testid="analysis-results">
                <h4 className="font-medium mb-2">Sequence Analysis</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-zinc-500">Length:</span>
                    <span className="ml-2 font-mono">{analysisResults.length} bp</span>
                  </div>
                  <div>
                    <span className="text-zinc-500">GC Content:</span>
                    <span className="ml-2 font-mono">{analysisResults.gc_content}%</span>
                  </div>
                  <div>
                    <span className="text-zinc-500">A:</span>
                    <span className="ml-2 font-mono">{analysisResults.composition?.A}</span>
                  </div>
                  <div>
                    <span className="text-zinc-500">T:</span>
                    <span className="ml-2 font-mono">{analysisResults.composition?.T}</span>
                  </div>
                  <div>
                    <span className="text-zinc-500">G:</span>
                    <span className="ml-2 font-mono">{analysisResults.composition?.G}</span>
                  </div>
                  <div>
                    <span className="text-zinc-500">C:</span>
                    <span className="ml-2 font-mono">{analysisResults.composition?.C}</span>
                  </div>
                </div>
                <div className="mt-4">
                  <Label className="text-xs">Reverse Complement</Label>
                  <div className="flex items-start gap-2 mt-1">
                    <div className="sequence-viewer flex-1 text-xs">
                      {formatSequence(analysisResults.reverse_complement)}
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => copyToClipboard(analysisResults.reverse_complement)}>
                      <Copy size={14} />
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Primer Results */}
            {primerResults && (
              <div className="mt-4 p-4 bg-zinc-50 rounded-sm" data-testid="primer-results">
                <h4 className="font-medium mb-2">Designed Primers</h4>
                <div className="space-y-3">
                  <div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Forward Primer</span>
                      <Button size="sm" variant="ghost" onClick={() => copyToClipboard(primerResults.forward?.sequence)}>
                        <Copy size={14} />
                      </Button>
                    </div>
                    <div className="font-mono text-sm bg-slate-800 p-2 rounded-sm border border-zinc-200 mt-1">
                      5' - {primerResults.forward?.sequence} - 3'
                    </div>
                    <div className="text-xs text-zinc-500 mt-1">
                      Tm: {primerResults.forward?.tm}°C | GC: {primerResults.forward?.gc_content}% | Length: {primerResults.forward?.length} bp
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Reverse Primer</span>
                      <Button size="sm" variant="ghost" onClick={() => copyToClipboard(primerResults.reverse?.sequence)}>
                        <Copy size={14} />
                      </Button>
                    </div>
                    <div className="font-mono text-sm bg-slate-800 p-2 rounded-sm border border-zinc-200 mt-1">
                      5' - {primerResults.reverse?.sequence} - 3'
                    </div>
                    <div className="text-xs text-zinc-500 mt-1">
                      Tm: {primerResults.reverse?.tm}°C | GC: {primerResults.reverse?.gc_content}% | Length: {primerResults.reverse?.length} bp
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Codon Optimization Results */}
            {codonResults && (
              <div className="mt-4 p-4 bg-zinc-50 rounded-sm" data-testid="codon-results">
                <h4 className="font-medium mb-2">Codon Optimization ({codonResults.organism})</h4>
                <div className="space-y-3">
                  <div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Optimized Sequence</span>
                      <Button size="sm" variant="ghost" onClick={() => copyToClipboard(codonResults.optimized_sequence)}>
                        <Copy size={14} />
                      </Button>
                    </div>
                    <div className="sequence-viewer text-xs mt-1">
                      {formatSequence(codonResults.optimized_sequence)}
                    </div>
                    <div className="text-xs text-zinc-500 mt-1">
                      Original GC: {codonResults.original_gc}% → Optimized GC: {codonResults.optimized_gc}%
                    </div>
                  </div>
                  <div>
                    <span className="text-sm font-medium">Protein Sequence</span>
                    <div className="font-mono text-sm bg-slate-800 p-2 rounded-sm border border-zinc-200 mt-1 break-all">
                      {codonResults.protein_sequence}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Search */}
      <div className="card-flat p-4 mb-6">
        <div className="relative max-w-md">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            type="text"
            placeholder="Search sequences..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 input-field w-full"
            data-testid="search-sequences"
          />
        </div>
      </div>

      {/* Sequences List */}
      {filteredSequences.length > 0 ? (
        <div className="space-y-4">
          {filteredSequences.map((seq) => (
            <div key={seq.id} className="card-flat p-4" data-testid={`sequence-card-${seq.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="heading-card text-slate-100">{seq.name}</h3>
                    <span className="text-xs bg-zinc-100 text-zinc-600 px-2 py-0.5 rounded-sm uppercase">
                      {seq.sequence_type}
                    </span>
                  </div>
                  {seq.description && <p className="text-sm text-zinc-500 mt-1">{seq.description}</p>}
                  <div className="flex items-center gap-4 mt-2 text-sm text-zinc-500">
                    <span>{seq.length} bp</span>
                    {seq.organism && <span>{seq.organism}</span>}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => { setToolSequence(seq.sequence); setShowToolsPanel(true); }}
                  >
                    <Lightning size={14} className="mr-1" /> Use in Tools
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => copyToClipboard(seq.sequence)}>
                    <Copy size={14} />
                  </Button>
                </div>
              </div>
              <div className="sequence-viewer mt-3 max-h-32 overflow-hidden text-xs">
                {colorizeSequence(seq.sequence?.substring(0, 300))}
                {seq.sequence?.length > 300 && <span className="text-zinc-400">...</span>}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="empty-state card-flat">
          <Dna size={48} className="text-zinc-300 mb-4" />
          <h3 className="heading-card text-slate-100 mb-2">No sequences yet</h3>
          <p className="text-sm text-zinc-500 mb-4">Start building your sequence library</p>
          <Button className="btn-primary" onClick={() => setShowNewForm(true)}>Add Sequence</Button>
        </div>
      )}
    </div>
  );
};

export default SequencesPage;
